import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Play, Trophy, RefreshCw, Settings2 } from "lucide-react";
import { useCourseData } from "@/hooks/useCourseData";
import { LiveScorecard } from "@/components/live/LiveScorecard";
import { PlayerTotals } from "@/components/live/PlayerTotals";
import { FoursomeSection } from "@/components/live/FoursomeSection";
import { IndividualSection } from "@/components/live/IndividualSection";
import { UnitsOyesSummary } from "@/components/live/UnitsOyesSummary";

export default function LiveSummary() {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [expandedFoursome, setExpandedFoursome] = useState(null);
  const [expandedIndividual, setExpandedIndividual] = useState(null);
  const [showScorecard, setShowScorecard] = useState(false);

  const fetchRound = useCallback(async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, { credentials: "include" });
      if (response.ok) {
        setRound(await response.json());
      } else {
        toast.error("Error al cargar la ronda");
        navigate("/dashboard");
      }
    } catch {
      toast.error("Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await fetch(`${API}/rounds/${roundId}/recalculate`, { method: "POST", credentials: "include" });
      if (response.ok) {
        setRound(await response.json());
        toast.success("Resultados recalculados");
      } else {
        toast.error("Error al recalcular");
      }
    } catch (error) {
      toast.error("Error al recalcular");
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => { fetchRound(); }, [fetchRound]);

  const { coursesData, coursesLoading } = useCourseData();
  const courseName = round?.course || "Guadalajara Country Club";
  const courseHoles = coursesData?.[courseName] || [];
  const startingHole = round?.starting_hole || 1;

  const basePlayers = useMemo(() => round?.players?.filter(p => p.is_base) || [], [round]);
  const par3Holes = useMemo(() => courseHoles.filter(h => h.par === 3).map(h => h.hole), [courseHoles]);
  const isVuelta1 = useCallback((holeNum) => startingHole === 1 ? holeNum <= 9 : holeNum >= 10, [startingHole]);

  const playerTotals = useMemo(() => {
    const totals = {};
    round?.players?.forEach(p => {
      totals[p.player_id] = { foursome: 0, individuals: 0, total: 0 };
    });
    round?.matchups?.forEach(m => {
      m.base_pair?.forEach(id => { if (totals[id]) totals[id].foursome += m.total_points; });
      m.secondary_pair?.forEach(id => { if (totals[id]) totals[id].foursome -= m.total_points; });
    });
    round?.individual_matchups?.forEach(m => {
      if (totals[m.player1_id]) totals[m.player1_id].individuals += m.total_points;
      if (totals[m.player2_id]) totals[m.player2_id].individuals -= m.total_points;
    });
    Object.keys(totals).forEach(id => {
      totals[id].total = totals[id].foursome + totals[id].individuals;
    });
    return totals;
  }, [round]);

  const totalUnits = useMemo(() => {
    const units = {};
    round?.players?.forEach(p => { units[p.player_id] = round?.total_units?.[p.player_id] || 0; });
    return units;
  }, [round]);

  const playedHoles = useMemo(() => 
    courseHoles.filter((_, i) => round?.players?.[0]?.scores?.some(s => s.hole_number === i + 1)),
    [courseHoles, round]
  );

  const totalPar = useMemo(() => playedHoles.reduce((s, h) => s + h.par, 0), [playedHoles]);

  if (loading || coursesLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-pulse text-[#1B4D3E]">Cargando...</div>
      </div>
    );
  }

  const completedHoles = round?.players?.[0]?.scores?.length || 0;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-[#1B4D3E] text-white px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => navigate(`/round/${roundId}/play`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Resumen en Vivo</h1>
            <p className="text-xs text-white/70">{round?.name} - Hoyo {completedHoles}/18</p>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={handleRecalculate} disabled={recalculating} data-testid="recalculate-btn" title="Refrescar">
            <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => navigate(`/round/${roundId}/doubles`)} data-testid="config-doubles-btn" title="Dobles">
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button onClick={() => navigate(`/round/${roundId}/play`)} className="btn-primary text-sm px-3" data-testid="continue-play-btn">
            <Play className="w-4 h-4 mr-1" /> Jugar
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-3 py-4">
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-[#64748B] mb-1">
            <span>Progreso</span>
            <span>{completedHoles}/18 hoyos</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#1B4D3E] transition-all duration-500" style={{ width: `${(completedHoles / 18) * 100}%` }}></div>
          </div>
        </div>

        <LiveScorecard
          round={round}
          playedHoles={playedHoles}
          totalPar={totalPar}
          showScorecard={showScorecard}
          setShowScorecard={setShowScorecard}
          courseHoles={courseHoles}
        />

        <PlayerTotals round={round} playerTotals={playerTotals} />

        <FoursomeSection
          round={round}
          basePlayers={basePlayers}
          expandedFoursome={expandedFoursome}
          setExpandedFoursome={setExpandedFoursome}
          isVuelta1={isVuelta1}
        />

        <UnitsOyesSummary round={round} totalUnits={totalUnits} par3Holes={par3Holes} />

        <IndividualSection
          round={round}
          expandedIndividual={expandedIndividual}
          setExpandedIndividual={setExpandedIndividual}
          isVuelta1={isVuelta1}
        />

        {completedHoles === 18 && round?.status !== "completed" && (
          <Button onClick={() => navigate(`/round/${roundId}/results`)} className="btn-primary w-full py-5" data-testid="view-results-btn">
            <Trophy className="w-5 h-5 mr-2" /> Ver Resultados Finales
          </Button>
        )}
      </main>
    </div>
  );
}
